import { useApiClient } from '@klareospace/pharos-lib-front-utils';
import type { UpsertUserPreference, UserPreference } from '@klareospace/pharos-lib-types';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { createUserPreferenceApi, userPreferenceKeys } from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseUpsertUserPreferenceOptions {
  /** Overrides the default error toast — receives the raw mutation error. */
  onError?: (error: Error) => void;
  /** When true, suppresses the default success toast (e.g. for auto-saved, high-frequency preferences). */
  silentSuccess?: boolean;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUpsertUserPreference(
  options: UseUpsertUserPreferenceOptions = {},
): UseMutationResult<UserPreference, Error, UpsertUserPreference> {
  const { onError, silentSuccess = false } = options;
  const client = useApiClient('admin-auth');
  const api = createUserPreferenceApi(client);
  const queryClient = useQueryClient();
  const { t } = useTranslation('orders');

  return useMutation({
    mutationFn: (input: UpsertUserPreference): Promise<UserPreference> => api.upsertUserPreference(input),
    onError: (error): void => {
      if (onError) {
        onError(error);
        return;
      }
      toast.error(t('table.columns.toast.saveError'));
    },
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: userPreferenceKeys.lists() });
      if (!silentSuccess) {
        toast.success(t('table.columns.toast.saveSuccess'));
      }
    },
  });
}
